import { useEffect } from 'react';
import TabPanel from '../../components/TabPanel';
import Grid from '@mui/material/Grid';
import TextInput from '../../components/TextInput';
import { Job } from '../../types/job';
import useConfigStyles from '../../components/useConfigStyles';
import { useVariablesContext } from '../../stores/variables-context';

interface JobTabPanelProps {
  job: Job;
  index: number;
  selectedTab: number;
  editable: boolean;
  errors?: Record<string, string[]>;
  onJobUpdate?: (job: Job, fieldName: string) => void;
}

function JobTabPanel({ job, index, selectedTab, editable, errors, onJobUpdate }: JobTabPanelProps): JSX.Element {
  const classes = useConfigStyles();

  const variablesContext = useVariablesContext();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!onJobUpdate) {
      return;
    }
    const { name, value } = event.target;
    onJobUpdate(
      {
        ...job,
        [name]: value,
      },
      name,
    );
  };

  const formatDate = (date: Date | undefined) => {
    if (date !== undefined && date !== null) return new Date(date).toISOString().substring(0, 10);
  };

  const handleNumberInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!onJobUpdate) {
      return;
    }
    const { name, valueAsNumber, value } = event.target;
    onJobUpdate(
      {
        ...job,
        [name]: value !== '' && Number.isNaN(valueAsNumber) ? 1 : valueAsNumber,
      },
      name,
    );
  };

  const handleSchoolYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!onJobUpdate) {
      return;
    }
    const { name, value } = event.target;

    if (job.SchoolYear.toString() != value) {
      const variables = job.Variables;
      const schoolYearIndex = variables.findIndex((v) => v.Name === 'SchoolYear');

      if (schoolYearIndex != -1) {
        variables.splice(schoolYearIndex, 1);
      }

      job.Variables = [{ Name: name, Value: value }, ...variables];
    }

    onJobUpdate(
      {
        ...job,
        [name]: value !== '' && Number.isNaN(value) ? undefined : parseInt(value),
      },
      name,
    );
  };

  useEffect(() => {
    // Set the SchoolYear variable with the Job.SchoolYear value
    const schoolYearVariable = variablesContext.data.find((v) => v.Name === 'SchoolYear');

    if (onJobUpdate && schoolYearVariable && job.Variables.findIndex((v) => v.Name === schoolYearVariable.Name) == -1) {
      onJobUpdate(
        {
          ...job,
          Variables: [...job.Variables, { Name: schoolYearVariable.Name, Value: job.SchoolYear }],
        },
        'Variables',
      );
    }
  });

  return (
    <TabPanel selectedValue={selectedTab} index={index} className={classes.container}>
      <Grid item className={classes.input} xs={12}>
        <TextInput name='Name' label='Name' value={job.Name} onChange={handleInputChange} errors={errors} editable={editable} />
      </Grid>
      <Grid item className={classes.input} xs={6}>
        <TextInput name='Description' label='Description' value={job.Description} onChange={handleInputChange} errors={errors} editable={editable} />
      </Grid>
      <Grid item className={classes.input} xs={12}>
        <TextInput name='SchoolYear' label='School Year' value={job.SchoolYear} onChange={handleSchoolYearChange} errors={errors} editable={editable}></TextInput>
      </Grid>
      <Grid item className={classes.input} xs={12}>
        <TextInput name='RowDataLimit' label='Row Data Limit' type='number' value={job.RowDataLimit} onChange={handleNumberInputChange} errors={errors} editable={editable} />
      </Grid>
    </TabPanel>
  );
}

JobTabPanel.defaultProps = {
  errors: {},
  onJobUpdate: () => {},
};

export default JobTabPanel;
